import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/providers/AuthProvider";

/**
 * Subscribes to Supabase Realtime changes on the user's data tables and
 * invalidates React Query caches so any mounted query refetches fresh data.
 *
 * Mounted once at the app root.
 */
const TABLES = [
  "customers",
  "payment_links",
  "profiles",
  "platform_connections",
  "email_logs",
  "sms_logs",
] as const;

export function useRealtimeSync() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const channel = supabase.channel(`realtime-sync-${user.id}`);

    TABLES.forEach((table) => {
      channel.on(
        "postgres_changes",
        { event: "*", schema: "public", table },
        () => {
          // Invalidate any query whose key starts with the table name
          queryClient.invalidateQueries({ queryKey: [table] });
        }
      );
    });

    channel.subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);
}
